import React, { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { authAPI } from '@/shared/services/api';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, Eye, EyeOff, Loader2, LockKeyhole, Package } from 'lucide-react';

const ResetPasswordPage = () => {
  const { t } = useTranslation(['auth', 'common']);
  const { token: pathToken } = useParams();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => pathToken || searchParams.get('token') || '', [pathToken, searchParams]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!token) {
      toast.error(t('auth:invalidResetLink'));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t('auth:passwordsDoNotMatch'));
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setCompleted(true);
      toast.success(t('auth:resetPasswordSuccessTitle'));
    } catch (error) {
      toast.error(error.message || t('auth:invalidResetLink'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md bg-card border-border backdrop-blur">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-accent/80 flex items-center justify-center shadow-lg shadow-accent/20">
            <Package className="h-8 w-8 text-accent-foreground" />
          </div>
          <CardTitle className="text-3xl font-bold text-foreground font-['Space_Grotesk'] italic leading-none">EzERP</CardTitle>
          <CardDescription className="text-muted-foreground text-[10px] font-bold tracking-widest uppercase mt-1">
            {t('auth:passwordRecovery')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {completed ? (
            <div className="space-y-5 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h1 className="text-xl font-bold text-foreground">{t('auth:resetPasswordSuccessTitle')}</h1>
                <p className="text-sm text-muted-foreground">{t('auth:resetPasswordSuccessSubtitle')}</p>
              </div>
              <Button asChild className="w-full bg-accent hover:bg-accent/90 text-accent-foreground h-12 rounded-xl font-bold">
                <Link to="/login">{t('auth:signIn')}</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 text-accent">
                  <LockKeyhole className="h-6 w-6" />
                </div>
                <h1 className="text-xl font-bold text-foreground">{t('auth:resetPasswordTitle')}</h1>
                <p className="text-sm text-muted-foreground">{t('auth:resetPasswordSubtitle')}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-muted-foreground font-bold text-[10px] uppercase tracking-widest">
                  {t('auth:newPassword')}
                </Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={8}
                    className="bg-muted/50 border-border text-foreground placeholder:text-muted-foreground pr-10 h-12 rounded-xl focus-visible:ring-accent/50"
                    data-testid="reset-password-new"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-muted-foreground font-bold text-[10px] uppercase tracking-widest">
                  {t('auth:confirmPassword')}
                </Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  className="bg-muted/50 border-border text-foreground placeholder:text-muted-foreground h-12 rounded-xl focus-visible:ring-accent/50"
                  data-testid="reset-password-confirm"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-accent hover:bg-accent/90 text-accent-foreground h-12 rounded-xl font-bold shadow-lg shadow-accent/20"
                disabled={loading}
                data-testid="reset-password-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth:resettingPassword')}
                  </>
                ) : (
                  t('auth:resetPassword')
                )}
              </Button>
              <Button asChild variant="ghost" className="w-full h-10 rounded-xl font-semibold">
                <Link to="/login">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('auth:backToLogin')}
                </Link>
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
